// packages/web/src/components/ProductCard.tsx
import React from 'react'
import { Link } from '@tanstack/react-router'
import { Sparkles, ShoppingBag, Award } from 'lucide-react'
import type { Product } from '@buddys-choice/shared'
import { ProductImage } from './ProductImage'

interface ProductCardProps {
  product: Product
}

export const ProductCard: React.FC<ProductCardProps> = ({ product }) => {
  const inStock = product.inStock ?? (product.totalStock ?? 0) > 0

  const rawComparePrice = (product as { compareAtPrice?: number | null; compare_at_price?: number | null }).compareAtPrice ?? (product as { compare_at_price?: number | null }).compare_at_price
  const compareAtPrice = rawComparePrice && rawComparePrice > product.basePrice
    ? rawComparePrice
    : product.basePrice > 0
    ? Math.round(product.basePrice * 1.25)
    : null

  const discountPercent = compareAtPrice && compareAtPrice > product.basePrice
    ? Math.round(((compareAtPrice - product.basePrice) / compareAtPrice) * 100)
    : 0

  const formattedPrice = new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(product.basePrice)

  const formattedComparePrice = compareAtPrice
    ? new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: 'INR',
        maximumFractionDigits: 0,
      }).format(compareAtPrice)
    : null

  return (
    <div className="group glass-luxury-card overflow-hidden flex flex-col justify-between transition-all duration-300 relative border-[var(--gold-main)]/20 hover:border-[var(--gold-main)] hover:-translate-y-1 rounded-2xl bg-[var(--bg-surface)]">
      <Link to="/product/$productId" params={{ productId: product.id }} className="block">
        <div className="relative aspect-square w-full overflow-hidden bg-[var(--bg-main)] border-b border-[var(--border)]">
          <ProductImage
            src={product.heroImageUrl}
            alt={product.name}
            className="w-full h-full"
            imgClassName="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-700"
          />
          <div className="absolute top-2 left-2 flex flex-col gap-1 z-10">
            <span className="badge badge-hallmark text-[9px] px-2 py-0.5 shadow-lg">
              <Award className="w-2.5 h-2.5" /> GOLD PLATED
            </span>
            {product.isFeatured && (
              <span className="badge bg-[var(--gold-main)] text-[var(--gold-contrast)] text-[8px] px-1.5 py-0.5 flex items-center gap-0.5 w-fit">
                <Sparkles className="w-2.5 h-2.5" /> FEATURED
              </span>
            )}
          </div>
          {discountPercent > 0 && (
            <div className="absolute top-2 right-2 z-10">
              <span className="bg-emerald-500 text-white font-mono font-extrabold text-[9px] px-2 py-1 rounded-lg shadow-lg flex items-center gap-0.5">
                {discountPercent}% OFF
              </span>
            </div>
          )}
        </div>
      </Link>

      <div className="p-3 flex flex-col flex-1 justify-between gap-2 bg-[var(--bg-surface)]/60">
        <div>
          <div className="flex items-center justify-between text-[10px] text-[var(--text-muted)] font-mono mb-1">
            <span className="truncate max-w-[85px]">SKU: {product.sku}</span>
          </div>
          <Link to="/product/$productId" params={{ productId: product.id }}>
            <h3 className="text-xs font-title font-bold text-[var(--text-primary)] line-clamp-2 h-8 leading-snug group-hover:text-[var(--gold-main)] transition">
              {product.name}
            </h3>
          </Link>
        </div>

        <div className="pt-2 border-t border-[var(--border)] flex items-center justify-between gap-1">
          <div className="flex flex-col">
            {formattedComparePrice && (
              <span className="text-[11px] font-mono text-[var(--text-muted)] line-through leading-none decoration-red-400/80 decoration-1">
                {formattedComparePrice}
              </span>
            )}
            <span className="text-xs sm:text-sm font-title font-bold gold-shimmer-text leading-tight">{formattedPrice}</span>
          </div>
          <Link
            to="/product/$productId"
            params={{ productId: product.id }}
            className="btn-gold-outline text-[10px] py-1 px-2.5 rounded-lg flex items-center gap-1 shrink-0"
          >
            <ShoppingBag className="w-3 h-3" />
            {inStock ? 'Add to Bag' : 'Pre-order'}
          </Link>
        </div>
      </div>
    </div>
  )
}
